
import { defineComponent, PropType } from 'vue'

interface TooltipPosition {
  x: number
  y: number
}

interface ResponsiveConfig {
  mobile: number // < 640px
  tablet: number // 640px - 1024px
  desktop: number // > 1024px
}

export default defineComponent({
  name: 'UITooltip',
  props: {
    triggerText: {
      type: String,
      default: ''
    },
    content: {
      type: String,
      default: ''
    },
    preferredPosition: {
      type: String as PropType<'top' | 'bottom' | 'auto'>,
      default: 'top',
      validator: (value: string) => ['top', 'bottom', 'auto'].includes(value)
    },
    tooltipClass: {
      type: String,
      default: ''
    },
    edgePadding: {
      type: Number,
      default: 16
    },
    responsiveWidth: {
      type: Object as PropType<Partial<ResponsiveConfig>>,
      default: () => ({})
    },
    useViewportPercentage: {
      type: Boolean,
      default: true
    },
    showDelay: {
      type: Number,
      default: 300
    },
    hideDelay: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showTooltip: false,
      tooltipPosition: { x: 0, y: 0 } as TooltipPosition,
      showTimeout: null as ReturnType<typeof setTimeout> | null,
      hideTimeout: null as ReturnType<typeof setTimeout> | null,
      viewportWidth: 0,
      viewportHeight: 0
    }
  },
  computed: {
    // Responsive max width based on screen size
    responsiveMaxWidth(): number {
      const defaultConfig: ResponsiveConfig = {
        mobile: 280, // ~90% of 320px (small mobile)
        tablet: 480, // Good for tablet reading
        desktop: 600 // Comfortable desktop reading
      }

      const config = { ...defaultConfig, ...this.responsiveWidth }

      if (this.useViewportPercentage) {
        // Use percentage of viewport width with responsive caps
        if (this.viewportWidth < 640) {
          return Math.min(this.viewportWidth * 0.9, config.mobile)
        } else if (this.viewportWidth < 1024) {
          return Math.min(this.viewportWidth * 0.75, config.tablet)
        } else {
          return Math.min(this.viewportWidth * 0.6, config.desktop)
        }
      }

      // Fixed responsive breakpoints
      if (this.viewportWidth < 640) {
        return config.mobile
      } else if (this.viewportWidth < 1024) {
        return config.tablet
      } else {
        return config.desktop
      }
    },

    tooltipStyle() {
      // Estimate tooltip width
      const estimatedWidth = this.estimateTooltipWidth()
      const maxAllowedWidth = Math.min(estimatedWidth, this.responsiveMaxWidth)

      let x = this.tooltipPosition.x
      const y = this.tooltipPosition.y - 8

      // Horizontal boundary check
      if (x + maxAllowedWidth > this.viewportWidth - this.edgePadding) {
        x = this.viewportWidth - maxAllowedWidth - this.edgePadding
      }
      if (x < this.edgePadding) {
        x = this.edgePadding
      }

      // Vertical positioning logic
      const tooltipHeight = 60 // Estimated height
      const shouldShowBelow = this.shouldShowBelow(y, tooltipHeight)

      const baseStyle = {
        left: `${x}px`,
        maxWidth: `${Math.min(maxAllowedWidth, this.viewportWidth - 2 * this.edgePadding)}px`,
        zIndex: 9999
      }

      if (shouldShowBelow) {
        return {
          ...baseStyle,
          top: `${this.tooltipPosition.y + 32}px`,
          transform: 'translateY(0)'
        }
      }

      return {
        ...baseStyle,
        top: `${y}px`,
        transform: 'translateY(-100%)'
      }
    },

    tooltipArrowClass(): string {
      const y = this.tooltipPosition.y - 8
      const tooltipHeight = 60

      if (this.shouldShowBelow(y, tooltipHeight)) {
        return 'bottom-full border-t-0 border-b-4 border-b-[#2c303a]'
      }
      return 'top-full border-t-4 border-t-[#2c303a]'
    },

    tooltipArrowStyle() {
      const estimatedWidth = this.estimateTooltipWidth()
      const maxAllowedWidth = Math.min(estimatedWidth, this.responsiveMaxWidth)

      // Calculate arrow position
      const originalX = this.tooltipPosition.x
      const tooltipX = Math.max(
        this.edgePadding,
        Math.min(originalX, this.viewportWidth - this.edgePadding - maxAllowedWidth)
      )
      const arrowOffset = Math.max(8, Math.min(originalX - tooltipX + 16, maxAllowedWidth - 16))

      return {
        left: `${arrowOffset}px`
      }
    }
  },
  mounted() {
    this.updateViewportSize()
    window.addEventListener('resize', this.updateViewportSize)
  },
  beforeDestroy() {
    // Clean up timeouts and event listeners
    if (this.showTimeout) clearTimeout(this.showTimeout)
    if (this.hideTimeout) clearTimeout(this.hideTimeout)
    window.removeEventListener('resize', this.updateViewportSize)
  },
  methods: {
    updateViewportSize() {
      this.viewportWidth = window.innerWidth
      this.viewportHeight = window.innerHeight
    },

    handleMouseEnter(event: MouseEvent) {
      // Clear any existing hide timeout
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout)
        this.hideTimeout = null
      }

      const rect = (event.currentTarget as Element).getBoundingClientRect()
      this.tooltipPosition = {
        x: rect.left,
        y: rect.top
      }

      if (this.showDelay > 0) {
        this.showTimeout = setTimeout(() => {
          this.showTooltip = true
        }, this.showDelay)
      } else {
        this.showTooltip = true
      }
    },

    handleMouseLeave() {
      // Clear any existing show timeout
      if (this.showTimeout) {
        clearTimeout(this.showTimeout)
        this.showTimeout = null
      }

      if (this.hideDelay > 0) {
        this.hideTimeout = setTimeout(() => {
          this.showTooltip = false
        }, this.hideDelay)
      } else {
        this.showTooltip = false
      }
    },

    estimateTooltipWidth(): number {
      // More accurate width estimation without artificial caps
      if (this.$slots.content) {
        // For slot content, estimate based on typical content
        // Let the responsive system handle the maximum
        return this.responsiveMaxWidth
      }

      if (this.content) {
        // Account for HTML tags and padding
        const textLength = this.content.replace(/<[^>]*>/g, '').length
        const estimatedWidth = textLength * 7 + 40
        // Don't cap here - let responsive system decide
        return Math.min(estimatedWidth, this.responsiveMaxWidth)
      }

      return 200 // Default width for empty content
    },

    shouldShowBelow(y: number, tooltipHeight: number): boolean {
      if (this.preferredPosition === 'bottom') return true
      if (this.preferredPosition === 'top') return false

      // Auto positioning based on available space
      return y < this.edgePadding + tooltipHeight
    }
  }
})
